import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import FocusLock from '@ingka/focus-lock';
import ArrowRightIcon from '@ingka/ssr-icon/paths/arrow-right';
import CloseIcon from '@ingka/ssr-icon/paths/cross';
import { styled } from 'styled-components';
import { SkapaButton, SkapaColors, SkapaText, SkapaTokens } from '@inter-ikea-guida/skapa';
import { useGuidaPromptContext } from '../context/promptContext';
import { useCommonTranslation } from '../hooks/useCommonTranslation';
import { useGuidaUIStates } from '../hooks/useGuidaUIStates';
import { SIDEBAR_WIDTH, UPPTACKA_KIOSK_SCALING_MULTIPLIER } from '../utils/constants';
const StyledOnboardingWrapper = styled.div `
  position: ${props => (props.$isSidebarMode ? 'absolute' : 'fixed')};
  z-index: ${props => (props.$isKiosk ? 'unset' : 500)};
  right: 0;
  top: ${props => (props.$isCompact ? 'unset' : 0)};
  bottom: ${props => (props.$isCompact ? 0 : 'unset')};

  display: flex;
  flex-direction: column;

  height: ${props => props.$isCompact ? 'calc(100% - 3rem)' : props.$isSidebarMode ? '100%' : '100vh'};

  width: ${props => props.$width};

  // The sheet below uses 8px radius, but if we use the same here one can see a hint of the white sheet below, hence 7px
  border-radius: ${props => props.$isSidebarMode ? SkapaTokens.radiusM : props.$isCompact ? `7px 7px 0 0` : `7px 0 0 7px`};

  background: linear-gradient(
    ${props => `${props.$gradientColors[0]}, ${props.$gradientColors[1]}`}
  );

  overflow: hidden;
`;
const StyledOnboardingContent = styled.div `
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${SkapaTokens.space150};
  height: 100%;
`;
const StyledBody = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${SkapaTokens.space100};

  * {
    color: ${props => props.$invertTheme ? SkapaColors.colourStaticWhite : SkapaColors.colourStaticBlack};
  }

  button span * {
    color: initial;
  }

  p,
  h2,
  h3 {
    margin: 0;
  }

  button:last-child {
    margin-top: ${SkapaTokens.space150};
  }

  padding: ${SkapaTokens.space250};
  padding-bottom: 0;
  padding-top: 0;
`;
const StyledBackdrop = styled.div `
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 100vw;
  background-color: rgba(var(--colour-static-black, 17, 17, 17), 0.4);
  z-index: 4;
`;
const StyledFocusLock = styled(FocusLock) `
  height: 100%;
`;
const StyledImage = styled.img `
  object-fit: cover;
  object-position: left top;
`;
const StyledOnboardingHeader = styled.div `
  display: flex;
  justify-content: end;
  align-items: center;
  flex-direction: row;

  min-height: 4.5rem;
`;
const StyledCloseButton = styled(SkapaButton) `
  padding: ${SkapaTokens.space100};
`;
export const GuidaOnboarding = ({ imgSrc, onDismissed, headingText, bodyText, buttonText, invertTheme, isKiosk, isUpptacka, gradientColors, showBackdrop }) => {
    const { isCompact, isSidebarMode } = useGuidaUIStates();
    const { openPrompt } = useGuidaPromptContext();
    const { t } = useCommonTranslation();
    const getOnboardingWidth = () => {
        if (isCompact) {
            return '100vw';
        }
        if (isUpptacka) {
            return `${SIDEBAR_WIDTH * UPPTACKA_KIOSK_SCALING_MULTIPLIER}px`;
        }
        return `${SIDEBAR_WIDTH}px`;
    };
    return (_jsxs(_Fragment, { children: [_jsx(StyledOnboardingWrapper, { "$isCompact": isCompact, "$isSidebarMode": isSidebarMode, "$width": getOnboardingWidth(), "$isKiosk": !!isKiosk, role: 'dialog', "aria-modal": 'true', "aria-label": headingText, "$gradientColors": gradientColors, children: _jsxs(StyledFocusLock, { locked: !isKiosk, capturePointerEvents: true, autoFocus: true, children: [_jsx("span", { role: 'note', "aria-label": t('Onboarding.AriaLabel'), tabIndex: 0, "data-autofocus": true }), _jsx(StyledOnboardingHeader, { children: !isKiosk && (_jsx(StyledCloseButton, { "aria-label": t('Onboarding.CloseAriaLabel'), iconOnly: true, ssrIcon: CloseIcon, type: 'tertiary', size: 'small', inverseTheme: true, onClick: openPrompt })) }), _jsxs(StyledOnboardingContent, { children: [_jsxs(StyledBody, { "$invertTheme": invertTheme, children: [_jsx(SkapaText, { textStyle: 'Heading.L', tagName: 'h2', children: headingText }), _jsx(SkapaText, { textStyle: 'Body.M', tagName: 'p', children: bodyText }), _jsx(SkapaButton, { onClick: onDismissed, type: 'primary', inverseTheme: true, text: buttonText, size: isCompact ? 'small' : 'medium', ssrIcon: ArrowRightIcon, iconPosition: 'trailing', dataTestId: 'onboarding-dismiss-btn' })] }), _jsx(StyledImage, { src: imgSrc, role: 'presentation' })] })] }) }), showBackdrop && _jsx(StyledBackdrop, {})] }));
};
//# sourceMappingURL=GuidaOnboarding.js.map